"use client";

import type { ProductType } from "@/lib/generated/product-types";
import { Button } from "@/components/ui/button";
import { GradingResults } from "./grading-results";
import { InvalidImageResults } from "./invalid-image-results";
import { FocusedRegion } from "./focused-region";
import type { GradingSessionState } from "./use-grading-session";

interface GradingOutcomeProps {
  grading: GradingSessionState;
  productType: ProductType | undefined;
  imagePreviews: Array<string>;
  onReset: () => void;
}

export function GradingOutcome(props: GradingOutcomeProps) {
  return (
    <FocusedRegion label="Grading results" block="start">
      <OutcomeBody {...props} />
    </FocusedRegion>
  );
}

function OutcomeBody({
  grading,
  productType,
  imagePreviews,
  onReset,
}: GradingOutcomeProps) {
  const analysis = grading.session?.analysisResults;
  const feedbackAccess = grading.attempt?.feedbackAccess;
  const sessionId = grading.attempt?.sessionId;

  if (grading.phase === "error" || !analysis || !feedbackAccess || !sessionId) {
    return (
      <GradingFailed
        message={grading.session?.error?.message}
        onReset={onReset}
      />
    );
  }

  if (analysis.isValidProduct === false) {
    return (
      <InvalidImageResults
        invalidReason={analysis.invalidReason}
        recommendations={analysis.recommendations}
        productTypeSlug={productType?.slug ?? analysis.detectedProductType}
        onReset={onReset}
      />
    );
  }

  return (
    <GradingResults
      results={analysis}
      buyerPrice={grading.session?.buyerPrice ?? null}
      feedbackAccess={feedbackAccess}
      sessionId={sessionId}
      onReset={onReset}
      imagePreviews={imagePreviews}
    />
  );
}

function GradingFailed({
  message,
  onReset,
}: {
  message: string | undefined;
  onReset: () => void;
}) {
  return (
    <div className="mx-auto max-w-2xl space-y-4 py-12 text-center">
      <p className="font-medium text-destructive-strong">
        {message ?? "Analysis failed. Please try again."}
      </p>
      <Button variant="outline" onClick={onReset}>
        Try again
      </Button>
    </div>
  );
}
